// src/screens/PayResultScreen.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle, XCircle, Loader2, Clock, RefreshCw, FileText, ArrowLeft, AlertCircle, Copy, Check, ExternalLink } from 'lucide-react';
import OrderServices from '../services/orderServices';

const PayResultScreen = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ✅ تغییر از userLogin به auth
  const { userInfo } = useSelector((state) => state.auth) || {};

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const dbParam = searchParams.get('db');

  useEffect(() => {
    if (!userInfo?.access) {
      navigate('/phone?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }

    if (!token) {
      setError('توکن پرداخت یافت نشد.');
      setLoading(false);
      return;
    }

    const inquiryPayment = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await OrderServices.inquiryPay(token, { userInfo, dispatch });
        setResult(response.data);
      } catch (err) {
        console.error('❌ Inquiry error:', err);
        setError(err.response?.data?.detail || err.message || 'خطا در استعلام پرداخت');
      } finally {
        setLoading(false);
      }
    };

    inquiryPayment();
  }, [token, userInfo, navigate, dispatch]);

  const handleCopyRef = () => {
    if (result?.refNumber) {
      navigator.clipboard.writeText(result.refNumber.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const goToOrder = () => {
    if (result?.orderId) {
      navigate(`/order/${result.orderId}`);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4EB] flex items-center justify-center px-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#8A9A7B] mx-auto" />
          <p className="text-gray-500 mt-4 text-sm">در حال استعلام وضعیت پرداخت...</p>
          <p className="text-xs text-gray-400 mt-1">لطفاً شکیبا باشید</p>
          {dbParam && (
            <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs text-gray-400">
              وضعیت دیتابیس: {dbParam === 'True' ? '✅ موفق' : '❌ ناموفق'}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F4EB] flex items-center justify-center px-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-[#D4837A] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">خطا در استعلام پرداخت</h3>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
          <div className="mt-6 space-y-3">
            <button onClick={handleRetry} className="w-full py-3 rounded-xl bg-[#8A9A7B] text-white font-bold hover:bg-[#6A7A5B] transition-colors flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4" /> تلاش مجدد
            </button>
            <button onClick={() => navigate('/profile')} className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" /> بازگشت به پروفایل
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isSuccess = result?.success === true;
  const statusCode = result?.status;
  const refNumber = result?.refNumber;
  const trackId = result?.trackId;
  const orderId = result?.orderId;
  const message = result?.message;

  return (
    <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#3D4A33] flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#8A9A7B]" />
            نتیجه پرداخت
          </h1>
          <p className="text-gray-500 text-sm mt-1">وضعیت نهایی تراکنش شما در زیر نمایش داده می‌شود.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className={`rounded-2xl border-2 p-6 text-center ${
            isSuccess ? 'border-[#7A9B8A] bg-[#F0F4EB]' : 'border-[#D4837A] bg-red-50'
          }`}>
            <div className="flex flex-col items-center">
              <div className="mb-3">
                {isSuccess ? (
                  <CheckCircle className="w-12 h-12 text-[#7A9B8A]" />
                ) : (
                  <XCircle className="w-12 h-12 text-[#D4837A]" />
                )}
              </div>
              <h2 className={`text-xl font-bold ${isSuccess ? 'text-[#7A9B8A]' : 'text-[#D4837A]'}`}>
                {isSuccess ? 'پرداخت موفق' : 'پرداخت ناموفق'}
              </h2>
              {message && <p className="text-sm text-gray-600 mt-1">{message}</p>}
              {dbParam && (
                <div className={`mt-3 px-3 py-1.5 rounded-lg text-xs font-medium ${
                  dbParam === 'True' ? 'bg-[#F0F4EB] text-[#7A9B8A]' : 'bg-red-50 text-[#D4837A]'
                }`}>
                  {dbParam === 'True' ? '✅ ثبت اطلاعات در دیتابیس: موفق' : '❌ ثبت اطلاعات در دیتابیس: ناموفق'}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50/80 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-bold text-gray-700">جزئیات تراکنش</h4>
            </div>
            <div className="px-4 divide-y divide-gray-100">
              <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-[#8A9A7B]" />
                  <span className="text-sm text-gray-500">کد وضعیت</span>
                </div>
                <span className="text-sm font-medium text-gray-800">{statusCode || '---'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-[#8A9A7B]" />
                  <span className="text-sm text-gray-500">شماره مرجع</span>
                </div>
                <span className="text-sm font-medium text-gray-800">{refNumber || '---'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2.5">
                  <ExternalLink className="w-4 h-4 text-[#8A9A7B]" />
                  <span className="text-sm text-gray-500">شناسه تراکنش</span>
                </div>
                <span className="text-sm font-medium text-gray-800">{trackId || '---'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-[#8A9A7B]" />
                  <span className="text-sm text-gray-500">شناسه سفارش</span>
                </div>
                <span className="text-sm font-medium text-gray-800">{orderId || '---'}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {refNumber && (
              <button onClick={handleCopyRef} className="w-full py-3 rounded-xl border-2 border-[#8A9A7B] text-[#8A9A7B] font-bold hover:bg-[#F0F4EB] transition-colors flex items-center justify-center gap-2">
                {copied ? (
                  <><Check className="w-4 h-4" /> کپی شد!</>
                ) : (
                  <><Copy className="w-4 h-4" /> کپی شماره مرجع</>
                )}
              </button>
            )}
            {isSuccess && orderId && (
              <button onClick={goToOrder} className="w-full py-3 rounded-xl bg-[#8A9A7B] text-white font-bold hover:bg-[#6A7A5B] transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-xl">
                <CheckCircle className="w-4 h-4" /> مشاهده جزئیات سفارش
              </button>
            )}
            {!isSuccess && (
              <button onClick={() => navigate('/')} className="w-full py-3 rounded-xl bg-[#8A9A7B] text-white font-bold hover:bg-[#6A7A5B] transition-colors flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> بازگشت به فروشگاه
              </button>
            )}
            <button onClick={() => navigate('/profile')} className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" /> بازگشت به پروفایل
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#8A9A7B]" />
                <span>زمان استعلام: {new Date().toLocaleTimeString('fa-IR')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>توکن: {token?.slice(0, 10)}...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayResultScreen;